import { useState, useEffect, useRef } from 'react';
import { Film, ChevronDown, ExternalLink, Star } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { getWatchedMovies } from '../../lib/supabase';

interface WatchedMovie {
  id: string;
  tmdb_id: number;
  title: string;
  year: number;
  poster_url: string | null;
  overview: string;
  watched_at: string;
  rating?: number;
  review?: string;
}

export function WatchedMoviesDropdown() {
  const [isOpen, setIsOpen] = useState(false);
  const [movies, setMovies] = useState<WatchedMovie[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadWatchedMovies();
    }
  }, [isOpen]);

  const loadWatchedMovies = async () => {
    try {
      setLoading(true);
      setError(null);
      const watchedList = await getWatchedMovies();
      setMovies(watchedList);
    } catch (err) {
      console.error('Error loading watched movies:', err);
      setError('Failed to load watched movies');
    } finally {
      setLoading(false);
    }
  };

  const renderRating = (rating?: number) => {
    if (!rating) return null;
    return (
      <div className="flex items-center gap-1">
        {[...Array(5)].map((_, index) => (
          <Star
            key={index}
            className={cn(
              'h-3 w-3',
              index < (rating || 0) ? 'fill-primary text-primary' : 'text-white/20'
            )}
          />
        ))}
      </div>
    );
  };

  return (
    <div ref={dropdownRef} className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'btn-quiet px-3 py-2 text-sm',
          isOpen && 'text-primary'
        )}
      >
        <Film className="h-4 w-4" />
        Watched Movies
        <ChevronDown className={cn(
          'h-4 w-4 transition-transform',
          isOpen && 'rotate-180 transform'
        )} />
      </button>

      {isOpen && (
        <div className="animate-fade-in absolute right-0 z-50 mt-2 w-80 overflow-hidden rounded-2xl border border-white/10 bg-popover shadow-deep">
          <div className="border-b border-white/[0.06] p-4">
            <h3 className="font-semibold text-foreground">My Watched Movies</h3>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="p-5 text-center">
                <div className="mx-auto h-1 w-28 overflow-hidden rounded-full bg-secondary">
                  <div className="animate-progress h-full rounded-full bg-primary" />
                </div>
              </div>
            ) : error ? (
              <div className="p-4 text-center text-sm text-red-400">
                {error}
              </div>
            ) : movies.length > 0 ? (
              <>
                <div className="divide-y divide-white/[0.05]">
                  {movies.slice(0, 5).map((movie) => (
                    <div key={movie.id} className="p-4 transition-colors hover:bg-white/[0.04]">
                      <div className="flex gap-3">
                        <div className="poster-frame h-16 w-12 flex-shrink-0 !rounded-md">
                          {movie.poster_url ? (
                            <img
                              src={movie.poster_url}
                              alt={movie.title}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center">
                              <Film className="h-6 w-6 text-muted-foreground/50" />
                            </div>
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <h4 className="truncate font-medium text-foreground">
                            {movie.title}
                          </h4>
                          <p className="text-sm text-muted-foreground">
                            {movie.year}
                          </p>
                          {renderRating(movie.rating)}
                          <p className="mt-1 text-xs text-muted-foreground/70">
                            Watched on {new Date(movie.watched_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="border-t border-white/[0.06] bg-white/[0.02] p-3.5">
                  <Link
                    to="/watched"
                    className="flex items-center justify-center gap-2 text-sm font-medium text-primary transition-colors hover:text-gold-soft"
                    onClick={() => setIsOpen(false)}
                  >
                    View All {movies.length > 0 && `(${movies.length})`}
                    <ExternalLink className="h-4 w-4" />
                  </Link>
                </div>
              </>
            ) : (
              <div className="p-6 text-center text-muted-foreground">
                <Film className="mx-auto mb-2 h-8 w-8 text-muted-foreground/50" />
                <p className="mb-4 text-sm">You haven't watched any movies yet</p>
                <Link
                  to="/watched"
                  className="text-sm font-medium text-primary transition-colors hover:text-gold-soft"
                  onClick={() => setIsOpen(false)}
                >
                  View Watched Movies
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}